'use client';

import {
  CardMedia,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
} from '@mui/material';

import { tss } from 'tss-react/mui';

import { forwardRef } from 'react';

import { Or2Card } from '@/components/Surface/Or2Card';
import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';

import { KeyAvatar } from '@/components/KeyBadge/KeyAvatar';

import { GroupOrServiceAvatar } from '@/components/Avatar/GroupOrServiceAvatar';

import { Or2Avatar } from '@/components/Avatar/Or2Avatar';
import { Or2ListItemButton } from '@/components/data-display/Or2ListItemButton';

import { NewsArticleHeader } from './article/NewsArticleHeader';
import { NewsArticleContent } from './article/NewsArticleContent';

const NEWS_ARTICLE_FRAGMENT = graphql(`
  fragment newsArticleFields on Article {
    ...newsArticleHeaderFields
    ...newsArticleContentFields
    _id
    title
    date
    url
    provider_name
    displayImages {
      newsfeed
    }
    knowledgeSuggestionsConnection {
      edges {
        node {
          title
          url
        }
      }
    }
    feed {
      _id
      name
      owner {
        ... on Group {
          _id
          name
          machineName
          iconUrl
        }
        ... on Service {
          _id
          name
          machineName
          iconUrl
        }
      }
    }
  }
`);

interface Props {
  article: DocumentType<typeof NEWS_ARTICLE_FRAGMENT>;
  hasKnowledge?: boolean;
  showProvider?: boolean;
}

const useStyles = tss.create(({ theme }) => {
  return {
    image: {
      height: '100%',
      [theme.breakpoints.down('sm')]: {
        height: '0px',
      },
    },
  };
});

export const NewsArticle = forwardRef<HTMLDivElement, Props>(
  ({ article, showProvider = true, hasKnowledge = false }, ref) => {
    const {
      _id: articleId,
      feed,
      title,
      provider_name,
      displayImages,
      url,
    } = article ?? {};

    const { owner: feedOwner } = feed || {};
    const image = displayImages?.newsfeed;
    const machineName = feedOwner?.machineName;
    const feedIconUrl = feedOwner?.iconUrl;
    const feedName = feedOwner?.name;

    const { classes } = useStyles();

    const knowledgeSuggestions =
      article?.knowledgeSuggestionsConnection?.edges?.map((edge) => edge?.node);

    return (
      <Or2Card ref={ref}>
        <Grid container>
          {image ? (
            <Grid size={{ xs: 12, sm: 4 }}>
              <CardMedia
                className={classes.image}
                image={image}
                title={title + ' image'}
              />
            </Grid>
          ) : null}
          <Grid size={{ xs: 12, sm: image ? 8 : 12 }}>
            <NewsArticleHeader article={article} showProvider={showProvider} />
            <NewsArticleContent article={article} />
            <List
              dense
              disablePadding
              sx={{ backgroundColor: 'secondary.light' }}
            >
              {knowledgeSuggestions.map((suggestion) => (
                <ListItem disableGutters disablePadding key={suggestion.title}>
                  <Tooltip
                    title={
                      hasKnowledge
                        ? undefined
                        : 'This article is only available with a GovernorHub Knowledge subscription'
                    }
                  >
                    <Or2ListItemButton
                      href={
                        (suggestion.url || '') +
                        '?origin=governorhub&sync=true&gh_feature=news'
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      color="primary"
                      variant="text"
                    >
                      <ListItemAvatar>
                        <KeyAvatar size={24} />
                      </ListItemAvatar>
                      <ListItemText primary={suggestion.title} />
                    </Or2ListItemButton>
                  </Tooltip>
                </ListItem>
              ))}
              {feedOwner ? (
                <ListItem disableGutters disablePadding>
                  <Or2ListItemButton
                    color="primary"
                    href={`/s/${machineName}/news/${articleId}`}
                  >
                    <ListItemAvatar>
                      <GroupOrServiceAvatar
                        iconUrl={feedIconUrl}
                        groupOrServiceName={feedName}
                        size={24}
                      />
                    </ListItemAvatar>
                    <ListItemText primary={'Read more...'} />
                  </Or2ListItemButton>
                </ListItem>
              ) : (
                <ListItem disableGutters disablePadding>
                  <Or2ListItemButton
                    color="primary"
                    href={url ?? ''}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ListItemAvatar>
                      <Or2Avatar
                        sx={{ height: 24, width: 24 }}
                        src={article.favicon_url || undefined}
                        alt={article.provider_name || ''}
                      >
                        {article?.provider_name?.charAt(0)}
                      </Or2Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`See more on ${provider_name || feed?.name}`}
                    />
                  </Or2ListItemButton>
                </ListItem>
              )}
            </List>
          </Grid>
        </Grid>
      </Or2Card>
    );
  },
);

NewsArticle.displayName = 'NewsArticle';
